import React, { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { TouchableOpacity, View } from 'react-native';
import styled from '@emotion/native';
import { BodyText, loraRegular } from '@/shared/components/StyledText';
import { StylistCalendar } from '@/shared/types/generated';
import { green, lightGreen, alphaBlack03, white } from '@/shared/styles/colors';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import { Feather } from '@expo/vector-icons';
import { Text } from 'react-native-paper';
import SectionLayout from '../SectionLayout';
import { IsDesktopProps } from '@/shared/styles/types';
import { SectionTitle } from '../styledComponents';
import useAvailabilityStore from '@/stores/useAvailabilityStore';
import { StepComponentProps } from '@/shared/types/availability';
import useDirty from '@/shared/hooks/useDirty';

const Availability = ({
  isAllCompleted,
  onSaveAvailability,
}: StepComponentProps) => {
  const { isDesktop } = useBreakpoint();
  const [loading, setLoading] = useState(false);

  const { selectedCalendarIds, setCalendarIds, calendars } =
    useAvailabilityStore(
      useShallow((state) => ({
        selectedCalendarIds: state.calendarIds,
        setCalendarIds: state.setCalendarIds,
        calendars: state.calendars,
      })),
    );

  const isDirty = useDirty(selectedCalendarIds);
  const isValid = selectedCalendarIds.length > 0;
  const isButtonDisabled = loading || !isDirty || !isValid;

  const toggleCalendarSelection = (calendarId: number) => {
    if (selectedCalendarIds.includes(calendarId)) {
      setCalendarIds(selectedCalendarIds.filter((id) => id !== calendarId));
    } else {
      setCalendarIds([...selectedCalendarIds, calendarId]);
    }
  };

  const saveAvailability = async () => {
    if (!onSaveAvailability) return;

    setLoading(true);
    try {
      // wait for the parent to finish (or throw)
      await onSaveAvailability();
    } finally {
      // always turn loading off, even if it errored
      setLoading(false);
    }
  };

  const existingCalendars =
    calendars?.filter((calendar): calendar is StylistCalendar =>
      Boolean(calendar),
    ) || [];

  return (
    <SectionLayout
      heading="Keep it conflict-free"
      description="Choose the calendar you use to manage your schedule. We'll use it to avoid conflicts when clients book you."
      buttonText={isAllCompleted ? 'Save Changes' : 'Continue'}
      onButtonPress={saveAvailability}
      isButtonDisabled={isButtonDisabled}
      isButtonLoading={loading}
      descriptionStyle={{ maxWidth: isDesktop ? 424 : 300 }}
      titleStyle={{ maxWidth: isDesktop ? '100%' : 233 }}
    >
      {existingCalendars.length > 0 ? (
        <>
          <CalendarSection>
            <SectionTitle>Calendars*</SectionTitle>
            <CalendarList isDesktop={isDesktop}>
              <View>
                {existingCalendars.map((calendar) => (
                  <CalendarRow key={calendar.id}>
                    <CheckboxWrapper>
                      <TouchableOpacity
                        onPress={() => toggleCalendarSelection(calendar.id)}
                      >
                        {selectedCalendarIds.includes(calendar.id) ? (
                          <CheckedBox>
                            <Feather name="check" size={20} color={white} />
                          </CheckedBox>
                        ) : (
                          <UncheckedBox />
                        )}
                      </TouchableOpacity>
                    </CheckboxWrapper>
                    <CalendarInfo>
                      <CalendarName>{calendar.name}</CalendarName>
                    </CalendarInfo>
                  </CalendarRow>
                ))}
              </View>
            </CalendarList>
          </CalendarSection>
        </>
      ) : (
        <NoCalendarsMessage>
          No calendars found. Please contact support to disconnect and reconnect
          your calendar.
        </NoCalendarsMessage>
      )}
    </SectionLayout>
  );
};

export default Availability;

const CalendarSection = styled.View({
  width: '100%',
  marginBottom: 36,
  marginTop: 16,
});

const CalendarList = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  width: '100%',
  marginTop: 8,
  paddingHorizontal: 28,
  paddingVertical: isDesktop ? 14 : 6,
  backgroundColor: alphaBlack03,
  borderRadius: 5,
  alignItems: isDesktop ? 'center' : 'flex-start',
  minWidth: isDesktop ? 548 : '100%',
}));

const CheckboxWrapper = styled.View({
  marginRight: 16,
});

const UncheckedBox = styled.View({
  width: 28,
  height: 28,
  borderWidth: 1,
  borderColor: green,
  borderRadius: 3,
  backgroundColor: 'transparent',
});

const CheckedBox = styled.View({
  width: 28,
  height: 28,
  borderWidth: 1,
  borderColor: green,
  borderRadius: 3,
  backgroundColor: green,
  justifyContent: 'center',
  alignItems: 'center',
});

const CalendarRow = styled.TouchableOpacity({
  flexDirection: 'row',
  paddingVertical: 16,
});

const CalendarInfo = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  flex: 1,
});

const CalendarName = styled(Text)({
  fontSize: 18,
  textTransform: 'uppercase',
  flexShrink: 1,
  color: green,
  flexWrap: 'wrap',
  ...loraRegular,
});

const NoCalendarsMessage = styled(BodyText)({
  color: lightGreen,
  textAlign: 'center',
  marginTop: 24,
});
